import { Loader, PinInput } from '@mantine/core';
import React from 'react';
import styled from 'styled-components';
import { Colors } from '../patterns/Colors';

const CODE_LENGTH = 6;

interface Props {
    email: string;
    code: string;
    onCodeChange: (code: string) => void;
    onSubmit: () => void;
    onResend: () => void;
    verifying: boolean;
    resending: boolean;
    error?: string | null;
}

export const OtpCodeForm = ({ email, code, onCodeChange, onSubmit, onResend, verifying, resending, error }: Props) => {

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (code.length < CODE_LENGTH || verifying) return;
        onSubmit();
    };

    const handleResend = (e: React.MouseEvent) => {
        e.preventDefault();
        if (resending) return;
        onResend();
    };

    return <Form onSubmit={handleSubmit}>
        <Intro>
            We sent a 6-digit code to <strong>{email}</strong>. Enter it below to sign in — it expires in 15 minutes.
        </Intro>
        <PinInputWrap>
            <PinInput
                length={CODE_LENGTH}
                type="number"
                inputMode="numeric"
                oneTimeCode
                autoFocus
                value={code}
                onChange={onCodeChange}
                onComplete={() => !verifying && onSubmit()}
                disabled={verifying}
                aria-label="Sign-in code"
            />
        </PinInputWrap>
        {verifying
            ? <Verifying><Loader size="sm" color="primary" /> Signing you in…</Verifying>
            : error && <ErrorText>{error}</ErrorText>}
        <Resend>
            Didn&apos;t get it?{' '}
            <ResendLink href="#" onClick={handleResend} aria-disabled={resending}>
                {resending ? 'Sending…' : 'Send a new code.'}
            </ResendLink>
        </Resend>
        <Shortcuts>
            <Shortcut href="https://outlook.office.com" target="_blank" rel="noopener noreferrer">
                <ShortcutImg src="https://s.spreo.io/site/img/outlook.svg" alt="Outlook" />
                <ShortcutLabel>Open Outlook</ShortcutLabel>
            </Shortcut>
            <Shortcut href="https://mail.google.com" target="_blank" rel="noopener noreferrer">
                <ShortcutImg src="https://s.spreo.io/site/img/google-mail.svg" alt="Gmail" />
                <ShortcutLabel>Open Gmail</ShortcutLabel>
            </Shortcut>
        </Shortcuts>
    </Form>;
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const Intro = styled.p`
    font-size: 0.875rem;
    color: ${Colors.text};
    margin: 0 0 1rem;
`;

const PinInputWrap = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
`;

const Verifying = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: ${Colors.text};
    margin-bottom: 0.75rem;
`;

const ErrorText = styled.p`
    font-size: 0.8125rem;
    color: #b91c1c;
    margin: 0 0 0.75rem;
    text-align: center;
`;

const Resend = styled.p`
    font-size: 0.875rem;
    color: ${Colors.text};
    margin: 0;
    text-align: center;
`;

const ResendLink = styled.a`
    color: ${Colors.primary};
    text-decoration: none;
    font-weight: 500;

    &:hover {
        text-decoration: underline;
    }

    &[aria-disabled="true"] {
        pointer-events: none;
        opacity: 0.6;
    }
`;

const Shortcuts = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1.25rem;
    margin-top: 1.5rem;
`;

const Shortcut = styled.a`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    text-decoration: none;
`;

const ShortcutImg = styled.img`
    width: 4rem;
    height: 4rem;
    border-radius: 0.75rem;
    border: 1px solid ${Colors.border};
    padding: 0.5rem;
    background: white;
    transition: transform 0.1s, box-shadow 0.1s;

    ${Shortcut}:hover & {
        transform: translateY(-1px);
        box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
    }
`;

const ShortcutLabel = styled.p`
    font-size: 0.75rem;
    color: ${Colors.textLight};
    margin: 0;
    text-align: center;
`;
